import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaPrint } from 'react-icons/fa';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { formatDateDDMMYY } from '../../../components/common/dateUtils';

const DeliveryOrderExtensionView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [extension, setExtension] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchExtensionDetails();
    // eslint-disable-next-line
  }, [id]);

  const fetchExtensionDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/sales/delivery-order-extension-get?extension_id=${id}`,
        { credentials: 'include' }
      );
      const data = await response.json();

      if (response.ok && data.data && data.data.length > 0) {
        setExtension(data.data[0]);
      } else {
        setError(data.error || 'Failed to fetch extension details');
        toast.error(data.error || 'Failed to fetch extension details');
      }
    } catch (error) {
      console.error('Error fetching extension details:', error);
      setError('Server Error');
      toast.error('Server Error');
    }
    setLoading(false);
  };

  const handlePrint = () => {
    window.print();
  };

  const formatExtensionNumber = (num) => {
    if (!num) return '01';
    return String(num).padStart(2, '0');
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto p-4 bg-white">
        <div className="text-center text-[#0c75b2] py-10">
          Loading extension details...
        </div>
      </div>
    );
  }

  if (error || !extension) {
    return (
      <div className="max-w-5xl mx-auto p-4 bg-white">
        <button
          onClick={() => navigate('/delivery-order-extension')}
          className="flex items-center gap-2 text-[#0c75b2] hover:text-[#0c75b2] mb-4"
        >
          <FaArrowLeft /> Back to List
        </button>
        <div className="text-center text-red-600 py-10">
          {error || 'Extension not found'}
        </div>
        <ToastContainer />
      </div>
    );
  }

  // Format dates
  const extendedUpToDate = extension.extended_up_to ? formatDateDDMMYY(extension.extended_up_to) : '';
  const doDate = extension.do_date ? formatDateDDMMYY(extension.do_date) : '';
  const dateOfExtension = extension.date_of_extension ? formatDateDDMMYY(extension.date_of_extension) : '';

  // Generate Extension Number format: DO_NUMBER/ext/01
  const extensionNumberFormatted = formatExtensionNumber(extension.extension_number);
  const doNumber = extension.do_number || '';
  
  // Generate Ext No: Just DO number followed by /ext/01
  const extNo = doNumber 
    ? `${doNumber}/EXT/${extensionNumberFormatted}`
    : '';

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white print:p-8 print:max-w-full" style={{ minHeight: '100vh' }}>
      <ToastContainer />
      
      {/* Action Buttons - Hide on print */}
      <div className="flex justify-between items-center mb-8 print:hidden">
        <button
          onClick={() => navigate('/delivery-order-extension')}
          className="flex items-center gap-2 text-[#0c75b2] hover:text-[#0c75b2] font-medium"
        >
          <FaArrowLeft /> Back to List
        </button>
        <button
          onClick={handlePrint}
          className="flex items-center gap-2 bg-[#0c75b2] text-white px-4 py-2 rounded hover:bg-[#0c75b2]"
        >
          <FaPrint /> Print
        </button>
      </div>

      {/* Extension Letter Document - Word Document Style */}
      <div className="bg-white print:bg-white" style={{ minHeight: '29.7cm', padding: '1cm 2cm 2.5cm 2cm' }}>
        {/* Letterhead Section - Logo Centered */}
        <div className="mb-6 print:mb-6 pb-3 print:pb-3 border-b print:border-b" style={{ borderColor: '#e5e7eb' }}>
          <div className="flex items-center justify-center pt-2 print:pt-2">
            <img src="/KribhcoLogo.png" alt="KRIBHCO Logo" className="h-12 w-auto print:h-10" style={{ maxHeight: '60px' }} />
          </div>
        </div>

        {/* Date and Ext No - Right Aligned, above To Section */}
        <div className="text-right text-sm print:text-sm mb-6 print:mb-6">
          <div className="mb-2 print:mb-2">
            <span className="font-semibold">Date of Extension:</span> {dateOfExtension}
          </div>
          <div>
            <span className="font-semibold">Extension No:</span> {extNo}
          </div>
        </div>

        {/* To Section */}
        <div className="mb-6 print:mb-6">
          <p className="text-sm font-semibold mb-2 print:mb-2">To,</p>
          <p className="text-sm font-semibold mb-2 print:mb-2">
            {extension.cma_name || 'NA'}
          </p>
          {extension.cma_address && (
            <p className="text-sm leading-relaxed print:leading-relaxed">{extension.cma_address}</p>
          )}
        </div>

        {/* Subject Line */}
        <div className="mb-6 print:mb-6">
          <p className="text-sm font-semibold print:font-semibold">
            <strong>Subject:</strong> Extension of Validity for Delivery Order No {doNumber}
          </p>
        </div>

        {/* Salutation */}
        <div className="mb-5 print:mb-5">
          <p className="text-sm print:text-sm">Dear Sir,</p>
        </div>

        {/* Body */}
        <div className="mb-8 text-sm leading-relaxed print:mb-8 print:leading-relaxed" style={{ lineHeight: '1.8' }}>
          <p className="mb-4 print:mb-4">
            The validity of Delivery Order No. <strong>{doNumber}</strong>, dated <strong>{doDate}</strong>, is hereby extended up to <strong>{extendedUpToDate}</strong>.
          </p>
          <p>
            Please facilitate the lifting process for pending quantity delivery accordingly.
          </p>
        </div>

        {/* Closing and Signature */}
        <div className="mt-12 print:mt-12">
          <div className="text-sm mb-5 print:mb-8">
            <div>
              Thanking you<br/>
              For KRIBHCO AGRI BUSINESS LIMITED
            </div>
            {/* Signature if available */}
            {extension.authority_signature && (
              <div>
                <img 
                  src={`${API_BASE_URL}/uploads/${extension.authority_signature}`} 
                  alt="Signature" 
                  className="h-12 w-auto mt-5 print:mt-8"
                  style={{height: '60px', maxWidth: '200px'}}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <div className="mt-2 print:mt-2 text-sm">Authorised Signatory</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeliveryOrderExtensionView;
